import React from 'react';
import {ButtonBase} from '@material-ui/core';
import {PlayCircleOutline, Delete, Visibility} from '@material-ui/icons';
import {ListItemKeySelector} from 'react-window';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {frameToSMPTE, FrameRate} from '@accurate-player/accurate-player-core';
import ListTable, {ListTableColumn, OnRowClickCallback} from '@pluto/player-list-table';
import {OnMouseButtonEvent, OnClickEvent} from '@pluto/@types';
import QCTooltip from '@pluto/qc-tooltip';

import TimecodeInput from 'VideoAudioQC/QCTabs/TimecodeInput/TimecodeInputContainer';
import {OnButtonClick, OnStartFrameChange} from './TableContainer';
import {TimelineMarker} from '../../../Timeline/TimelineTypes';
import {MoveMarker, GoToMarker} from '../../../icons';

import './Table.scss';

export type AdBreakData = {
    id: string;
    startSMPTE: string;
    start: number;
};

export interface TableProps {
    markers: TimelineMarker[];
    selectedMarkerId: string;
    adOnPlayingFrame?: TimelineMarker;
    selectedColumn: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    onRowClick: OnRowClickCallback;
    onInputClick: OnClickEvent;
    onPreviewClick: OnButtonClick;
    onMoveClick: OnButtonClick;
    onGoToClick: OnButtonClick;
    onDeleteClick: OnButtonClick;
    onStartFrameChange: OnStartFrameChange;
    jobDeliveryDate: string;
}

const mapAdBreakData = curry(
    (props: TableProps, adBreak: TimelineMarker): AdBreakData => {
        const {frameRate, dropFrame} = props;
        const {numerator, denominator} = frameRate;
        const {identifier, start} = adBreak;

        const frameRateValue = numerator / denominator;
        const startSMPTE = frameToSMPTE(start, frameRateValue, dropFrame);

        return {
            id: identifier,
            startSMPTE,
            start,
        };
    }
);

export const onButtonClick = wrap(
    (markerData: AdBreakData, onClick: OnButtonClick, event: OnMouseButtonEvent) => {
        event.stopPropagation();
        event.preventDefault();

        onClick(markerData);
    }
);

export const getPreviewButton = curry(
    (onPreviewClick: OnButtonClick, markerData: AdBreakData) => {
        const handlePreviewClick = onButtonClick(markerData, onPreviewClick);
        return (
            <QCTooltip title="Preview Breakpoint" arrow>
                <ButtonBase onClick={handlePreviewClick} aria-label="Preview Breakpoint">
                    <PlayCircleOutline />
                </ButtonBase>
            </QCTooltip>
        );
    }
);

export const getMoveButton = curry(
    (onMoveClick: OnButtonClick, markerData: AdBreakData) => {
        const handleMoveClick = onButtonClick(markerData, onMoveClick);
        return (
            <QCTooltip title="Move Breakpoint to CTI" arrow>
                <ButtonBase onClick={handleMoveClick} aria-label="Move Breakpoint to CTI">
                    <MoveMarker />
                </ButtonBase>
            </QCTooltip>
        );
    }
);

export const getGoToButton = curry(
    (onGoToClick: OnButtonClick, markerData: AdBreakData) => {
        const handleGoToClick = onButtonClick(markerData, onGoToClick);
        return (
            <QCTooltip title="Go To Breakpoint" arrow>
                <ButtonBase onClick={handleGoToClick} aria-label="Go To Breakpoint">
                    <GoToMarker />
                </ButtonBase>
            </QCTooltip>
        );
    }
);

export const getDeleteButton = curry(
    (onDeleteClick: OnButtonClick, markerData: AdBreakData) => {
        const handleDeleteClick = onButtonClick(markerData, onDeleteClick);
        return (
            <QCTooltip title="Delete Breakpoint" arrow>
                <ButtonBase onClick={handleDeleteClick} aria-label="Delete Breakpoint">
                    <Delete />
                </ButtonBase>
            </QCTooltip>
        );
    }
);

export type ButtonActions = {
    onPreviewClick: OnButtonClick;
    onMoveClick: OnButtonClick;
    onGoToClick: OnButtonClick;
    onDeleteClick: OnButtonClick;
};

export const getButtons = curry((actions: ButtonActions, markerData: AdBreakData) => {
    const {onPreviewClick, onGoToClick, onMoveClick, onDeleteClick} = actions;
    const moveButton = getMoveButton(onMoveClick, markerData);
    const goToButton = getGoToButton(onGoToClick, markerData);
    const previewButton = getPreviewButton(onPreviewClick, markerData);
    const deleteButton = getDeleteButton(onDeleteClick, markerData);

    return (
        <div>
            {moveButton}
            {goToButton}
            {previewButton}
            {deleteButton}
        </div>
    );
});

export interface StartFieldProps {
    dropFrame: boolean;
    duration: number;
    frameRateValue: number;
    selectedMarkerId: string;
    adOnPlayingFrame?: TimelineMarker;
    selectedColumn: string;
    columnKey: string;
    onStartFrameChange: OnStartFrameChange;
    onInputClick: OnClickEvent;
}

export const onFrameChange = curry(
    (onStartFrameChange: OnStartFrameChange, data: AdBreakData, start: number) => {
        onStartFrameChange(data, start);
    }
);

export const getStartField = curry((props: StartFieldProps, data: AdBreakData) => {
    const {
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        adOnPlayingFrame,
        selectedColumn,
        columnKey,
        onStartFrameChange,
        onInputClick,
    } = props;
    const adOnPlayingFrameSameMarker = adOnPlayingFrame
        ? adOnPlayingFrame.identifier === data.id
        : false;

    if (selectedMarkerId === data.id) {
        const isClicked = selectedColumn === columnKey;
        const handleStartFrameChange = onFrameChange(onStartFrameChange, data);
        return (
            <>
                <TimecodeInput
                    frame={data.start}
                    dropFrame={dropFrame}
                    duration={duration}
                    frameRateValue={frameRateValue}
                    autoFocus={isClicked}
                    onFrameChange={handleStartFrameChange}
                    onClick={onInputClick}
                />
                {adOnPlayingFrameSameMarker && <Visibility className="visibility-icon" />}
            </>
        );
    }

    return adOnPlayingFrameSameMarker ? (
        <>
            {data.startSMPTE}
            <Visibility className="visibility-icon" />
        </>
    ) : (
        data.startSMPTE
    );
});

export const getTable = (props: TableProps) => {
    const {
        markers,
        dropFrame,
        duration,
        frameRate,
        selectedMarkerId,
        adOnPlayingFrame,
        selectedColumn,
        onRowClick,
        onPreviewClick,
        onMoveClick,
        onGoToClick,
        onDeleteClick,
        onStartFrameChange,
        onInputClick,
        jobDeliveryDate,
    } = props;

    const handleDataMap = mapAdBreakData(props);
    const renderButton = getButtons({
        onPreviewClick,
        onMoveClick,
        onGoToClick,
        onDeleteClick,
    });
    const {numerator, denominator} = frameRate;
    const frameRateValue = numerator / denominator;
    const renderStartField = getStartField({
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        adOnPlayingFrame,
        selectedColumn,
        columnKey: 'startSMPTE',
        onStartFrameChange,
        onInputClick,
    });
    const markersLength = markers.length;

    const data = markers.map(handleDataMap);
    const startColumn: ListTableColumn = {
        dataKey: 'startSMPTE',
        label:
            markersLength > 1
                ? `Breakpoints (${markersLength})`
                : `Breakpoint (${markersLength})`,
        render: renderStartField,
    };
    const buttonsColumn: ListTableColumn = {
        dataKey: '',
        label: '',
        numeric: true,
        render: renderButton,
    };
    const columns: ListTableColumn[] = !jobDeliveryDate
        ? [startColumn, buttonsColumn]
        : [startColumn];
    const emptyText = 'No breakpoints added yet';

    return (
        <ListTable
            data={data}
            columns={columns}
            headerHeight={20}
            rowHeight={40}
            emptyText={emptyText}
            itemKey={getItemKey}
            selectedRowKey={selectedMarkerId}
            onRowClick={!jobDeliveryDate ? onRowClick : () => {}}
        />
    );
};

export const getItemKey: ListItemKeySelector = (
    index: number,
    data: {items: AdBreakData[]}
) => {
    return data.items[index].id;
};

export default function Table(props: TableProps) {
    const table = getTable(props);

    return <div className="ad-break-table-container">{table}</div>;
}
